// Agent presence on the daemon's screen: the per-turn presence-mode
// decision and the agent's pointer during a live lesson. A plain spoken
// answer shows nothing here: the pill or the character's bubble has it.
// See src/decisions/seats/presence-mode.ts and src/voice/live-teach.ts.

import { execFile } from "child_process"
import { promisify } from "util"
import type { DaemonConfig } from "@/daemon/config"
import { askSeat, getSeatMode } from "@/decisions/seat"
import {
  PRESENCE_MODE_SEAT, presenceModeQuestions, presenceModeState, toPresence,
  type PresenceDecision, type PresenceMode, type PresenceModeAnswers,
} from "@/decisions/seats/presence-mode"
import { HELPER } from "@/computer-use/screen"
import { LiveTeach, teachSystemPrompt, type TeachDeps, type TeachMode } from "@/voice/live-teach"
import { helperAct, readScreenView } from "@/voice/live-teach-screen"
import { IDLE_SECONDS, NO_POINTER, PresenceOverlay, presenceLook, reapPresence, type Presence, type PresenceLook } from "@/voice/presence"
import type { SpeechOut } from "@/voice/speaking-queue"
import type { LineModel } from "@/voice/talk-model"
import { talkSpeaker, type VoiceSettings } from "@/voice/agent-voice"
import { DEFAULT_LISTENER } from "@/voice/talk"

const run = promisify(execFile)
/** Budget for the per-turn decision; Jev answers in about half a second. */
const DECIDE_MS = 2_500
/** Held overlays are pinged this often, well inside the helper's idle timeout. */
const PING_MS = (IDLE_SECONDS * 1000) / 3
type Agents = DaemonConfig["agents"]

/** The one overlay an agent has on screen. */
interface Slot {
  presence: Presence
  ping: NodeJS.Timeout
}

export interface PresenceHostDeps {
  /** The global voice settings (agentx.json `voice`). */
  voiceSettings?: () => VoiceSettings
  overlay?: (look: PresenceLook, agentId: string) => Presence
  screen?: Pick<TeachDeps, "readScreen" | "act">
  frontmostApp?: () => Promise<string | null>
}

export interface PresenceTurn extends PresenceDecision {
  /** off: no seat, talk as before. shadow: logged only. active: acted on. */
  seat: "off" | "shadow" | "active"
  /** The app in front at this turn; a lesson the turn starts is about it. */
  app: string | null
}

async function helperFrontmostApp(): Promise<string | null> {
  try {
    const { stdout } = await run(HELPER, ["focused"], { timeout: 1500 })
    return (JSON.parse(stdout).app as string) || null
  } catch {
    return null
  }
}

export class PresenceHost {
  /** At most one overlay per agent. */
  private slots = new Map<string, Slot>()
  private lastMode = new Map<string, PresenceMode>()

  constructor(
    private agents: () => Agents,
    private log: (msg: string) => void,
    private deps: PresenceHostDeps = {},
  ) {}

  /** The presence-mode decision for one voice turn. Never throws; with the
   *  seat off or unsure, the answer is talk. */
  async decide(agentId: string, request: string): Promise<PresenceTurn> {
    const seat = getSeatMode(PRESENCE_MODE_SEAT)
    const look = presenceLook(agentId, this.agents()[agentId])
    if (seat === "off") return { ...toPresence(null, look.allowActions, request), seat, app: null }
    const app = await (this.deps.frontmostApp ?? helperFrontmostApp)()
    // It runs before the agent's turn, so it gets a hard budget: past it,
    // the turn goes ahead as talk rather than waiting on a slow backend.
    const ac = new AbortController()
    let timer: NodeJS.Timeout | undefined
    const result = await Promise.race([
      askSeat(
        PRESENCE_MODE_SEAT,
        presenceModeState({ agent: agentId, request, app, actionsAllowed: look.allowActions, previousMode: this.lastMode.get(agentId) ?? null }),
        presenceModeQuestions,
        { timeoutMs: DECIDE_MS, signal: ac.signal, incumbent: { mode: "talk" }, features: { agent: agentId, app: app ?? "none" } },
      ),
      new Promise<null>((r) => { timer = setTimeout(() => { ac.abort(); r(null) }, DECIDE_MS) }),
    ]).finally(() => clearTimeout(timer))
    const decision = toPresence((result?.answers as PresenceModeAnswers) ?? null, look.allowActions, request)
    this.lastMode.set(agentId, decision.mode)
    this.log(`[presence] ${agentId} seat=${seat} chose=${decision.chose ?? "-"} p=${decision.probability.toFixed(2)} → ${decision.mode}` +
      ` next=${decision.nextAction}${decision.persist ? " persist" : ""}${decision.override ? ` (${decision.override})` : ""}`)
    return { ...decision, seat, app }
  }

  /** A live lesson on this screen, in the agent's voice and cursor, about
   *  `app`; without one, about the app in front when the lesson starts. */
  lesson(agentId: string, goal: string, mode: TeachMode, speech: SpeechOut, model: (system: string) => LineModel, app?: string): LiveTeach {
    const agents = this.agents()
    const settings = this.deps.voiceSettings?.()
    const speaker = talkSpeaker(agentId, agents, false, settings)
    const listener = settings?.listener ?? DEFAULT_LISTENER
    const look = presenceLook(agentId, agents[agentId])
    return new LiveTeach(
      { goal, mode, speaker, actionsAllowed: look.allowActions, app, listener },
      {
        readScreen: this.deps.screen?.readScreen ?? readScreenView,
        act: this.deps.screen?.act ?? helperAct,
        // With `voice.pointer` off the lesson is spoken only.
        presence: settings?.pointer === false ? NO_POINTER : this.lessonPointer(agentId),
        speech,
        model: model(teachSystemPrompt(speaker.persona, listener)),
      },
    )
  }

  private lessonPointer(agentId: string): Presence {
    const slot = this.acquire(agentId)
    // The lesson ends by closing its presence: that releases the slot,
    // unless a newer lesson has already taken it over.
    return {
      moveTo: (r, o) => slot.presence.moveTo(r, o),
      say: (t) => slot.presence.say(t),
      clear: () => slot.presence.clear(),
      park: () => slot.presence.park(),
      close: () => { if (this.slots.get(agentId) === slot) this.hide(agentId) },
    }
  }

  /** Fade the agent's overlay out; the helper process exits. */
  hide(agentId: string): void {
    const slot = this.slots.get(agentId)
    if (!slot) return
    clearInterval(slot.ping)
    slot.presence.close()
    this.slots.delete(agentId)
  }

  close(): void {
    for (const id of [...this.slots.keys()]) this.hide(id)
  }

  /** Agents with an overlay on screen right now. */
  get onScreen(): string[] { return [...this.slots.keys()] }

  /** On daemon start: end overlays left by an earlier daemon or a dead parent. */
  reapOrphans(): void {
    const n = reapPresence()
    if (n) this.log(`[presence] ended ${n} leftover overlay${n === 1 ? "" : "s"}`)
  }

  /** The agent's overlay: the one already on screen, or a new one. Never a second. */
  private acquire(agentId: string): Slot {
    const cur = this.slots.get(agentId)
    // Its helper may have exited on its own (idle, crash): draw afresh.
    if (cur && cur.presence.alive === false) this.hide(agentId)
    else if (cur) return cur
    const presence = this.overlay(presenceLook(agentId, this.agents()[agentId]), agentId)
    const ping = setInterval(() => presence.ping?.(), PING_MS)
    ping.unref?.()
    const slot: Slot = { presence, ping }
    this.slots.set(agentId, slot)
    return slot
  }

  private overlay(look: PresenceLook, agentId: string): Presence {
    return this.deps.overlay?.(look, agentId) ?? new PresenceOverlay(look, HELPER, agentId)
  }
}
